<template>
  <div class="content-panel" style="max-width: 1000px;">
    <div class="panel-header">
      <div class="panel-icon">
        <v-icon size="32" color="#1e3a8a">mdi-file-document-multiple</v-icon>
      </div>
      <div class="panel-title-section">
        <h2 class="panel-title">파일 업로드하기</h2>
      </div>
    </div>

    <div class="panel-body">

      <div class="instruction-card" :class="{ 'move-bottom': hasUploaded }">
        <div class="instruction-icon">
          <v-icon color="#1e3a8a">mdi-monitor-arrow-down</v-icon>
        </div>
        <div class="instruction-content">
          <h4>파일 다운로드 안내</h4>
          <ol>
            <li>장기요양정보시스템 접속 후 우측 상단 <strong>기타</strong> &gt; <strong>엑셀 다운로드</strong>를 클릭합니다.</li>
            <li><strong>청구내역</strong>, <strong>청구내역상세</strong> 탭에서 각각 <strong>엑셀 다운로드</strong> 버튼을 눌러 2개 파일을 저장합니다.</li>
            <li>브라우저 저장 창에서 <strong>Ctrl + Shift + S</strong>로 파일을 저장해 주세요.</li>
          </ol>
          <p class="instruction-hint">청구내역, 청구내역상세 파일 2개를 모두 준비해야 다음 단계로 이동할 수 있습니다.</p>
        </div>
      </div>

      <div class="employees-table-card billing-data-card">
        <div class="employees-table-header">
          <div>
            <h4>업로드된 데이터</h4>
            <p class="header-subtitle">{{ periodLabel }} 기준</p>
          </div>
          <span class="employees-count">
            {{ totalDataCount  || 0}} 건
          </span>
        </div>

        <div v-if="isLoading" class="billing-loading">
          <v-progress-circular indeterminate color="#1e3a8a" size="32"></v-progress-circular>
          <span>데이터를 불러오는 중입니다...</span>
        </div>

        <template v-else>
          <template v-if="hasAnyData">
            <v-tabs v-model="activeTab" class="billing-tabs" density="compact">
              <v-tab value="summary">요약데이터</v-tab>
              <v-tab value="billing">청구내역 ({{ billingData.length }})</v-tab>
              <v-tab value="detail">청구내역상세 ({{ billingDetailData.length }})</v-tab>
            </v-tabs>

            <div class="billing-tab-panels">
              <div v-show="activeTab === 'summary'" class="summary-panel">
                <div class="summary-grid">
                  <div class="summary-card">
                    <div class="summary-icon">
                      <v-icon size="32" color="#1e3a8a">mdi-account-group</v-icon>
                    </div>
                    <div class="summary-content">
                      <div class="summary-label">총 수급자 수</div>
                      <div class="summary-value">{{ summaryData.totalPatients }}명</div>
                    </div>
                  </div>
                  <div class="summary-card">
                    <div class="summary-icon">
                      <v-icon size="32" color="#059669">mdi-account-heart</v-icon>
                    </div>
                    <div class="summary-content">
                      <div class="summary-label">총 요양보호사 수</div>
                      <div class="summary-value">{{ summaryData.totalCaregivers }}명</div>
                    </div>
                  </div>
                  <div class="summary-card">
                    <div class="summary-icon">
                      <v-icon size="32" color="#7c3aed">mdi-file-document</v-icon>
                    </div>
                    <div class="summary-content">
                      <div class="summary-label">총 수가</div>
                      <div class="summary-value">{{ formatCurrency(summaryData.totalSugaMoney) }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-show="activeTab === 'billing'" class="employees-table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>일자</th>
                      <th>수급자명</th>
                      <th>수급자 코드</th>
                      <th>요양보호사명</th>
                      <th>생년월일</th>
                      <th>요양보호사번호</th>
                      <th>구분</th>
                      <th>가족 여부</th>
                      <th>가족 관계</th>
                      <th>근무 시작</th>
                      <th>근무 종료</th>
                      <th>서비스 구분</th>
                      <th>수가 코드</th>
                      <th>수가명</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, index) in billingData" :key="row.leb_idx || index">
                      <td>{{ formatDate(row.leb_date) }}</td>
                      <td>{{ row.leb_patient_name }}</td>
                      <td>{{ row.leb_patient_code }}</td>
                      <td>{{ row.leb_caregiver_name }}</td>
                      <td>{{ formatDate(row.leb_caregiver_birth) }}</td>
                      <td>{{ row.leb_caregiver_code || '-' }}</td>
                      <td>{{ row.leb_caregiver_gubun || '-' }}</td>
                      <td>{{ formatYesNo(row.leb_familly_rel) }}</td>
                      <td>{{ row.leb_familly_who || '-' }}</td>
                      <td>{{ formatTime(row.leb_start_time) }}</td>
                      <td>{{ formatTime(row.leb_end_time) }}</td>
                      <td>{{ row.leb_service_gubun || '-' }}</td>
                      <td>{{ row.leb_suga_code || '-' }}</td>
                      <td>{{ row.leb_suga_name || '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div v-show="activeTab === 'detail'" class="employees-table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>처리상태</th>
                      <th>수급자명</th>
                      <th>수급자 코드</th>
                      <th>일자</th>
                      <th>시작시간</th>
                      <th>종료시간</th>
                      <th>요양보호사명</th>
                      <th>수가코드</th>
                      <th>수가명</th>
                      <th>수가</th>
                      <th>감산점수</th>
                      <th>감산비율</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, index) in billingDetailData" :key="row.lebd_idx || index">
                      <td>{{ row.lebd_pay_status || '-' }}</td>
                      <td>{{ row.lebd_patient_name }}</td>
                      <td>{{ row.lebd_patient_code }}</td>
                      <td>{{ formatDate(row.lebd_date) }}</td>
                      <td>{{ formatTime(row.lebd_start_time) }}</td>
                      <td>{{ formatTime(row.lebd_end_time) }}</td>
                      <td>{{ row.lebd_caregiver_name }}</td>
                      <td>{{ row.lebd_suga_code || '-' }}</td>
                      <td>{{ row.lebd_suga_name || '-' }}</td>
                      <td>{{ formatCurrency(row.lebd_suga_money) }}</td>
                      <td>{{ row.lebd_minus_point || '-' }}</td>
                      <td>{{ row.lebd_minus_rate || '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              </div>
          </template>
          <div v-else class="billing-empty">
            <v-icon size="32" color="#9ca3af">mdi-file-document-alert</v-icon>
            <p>아직 업로드된 데이터가 없습니다.</p>
          </div>
        </template>
      </div>

      <div
        v-if="hasAnyData"
        class="status-card"
        :class="isConfirmed ? 'info' : 'success'"
      >
        <v-icon :color="isConfirmed ? '#10b981' : '#00C73C'" size="24">
          {{ isConfirmed ? 'mdi-lock-check' : 'mdi-check-circle' }}
        </v-icon>
        <div class="status-card-text">
          <h4>{{ isConfirmed ? '데이터 확정됨' : '데이터 업로드 완료' }}</h4>
          <p>
            {{ isConfirmed
              ? '확정 상태예요. 해제 후에만 데이터를 수정하거나 재업로드할 수 있습니다.'
              : hasUploaded 
                ? '2개 파일이 모두 업로드되었습니다. 확정을 눌러 다음 단계로 진행하세요.'
                : `${uploadedFilesCount}개 파일만 업로드됨. 청구내역, 청구내역상세 파일 2개가 모두 필요합니다.` }}
          </p>
        </div>
        <div class="status-card-actions">
          <button class="btn-outline" @click="confirmReupload" :disabled="isConfirmed || isConfirming">
            재업로드
          </button>
          <template v-if="isConfirmed">
            <button class="btn-outline" @click="handleRelease" :disabled="isConfirming">
              <v-progress-circular
                v-if="isConfirming"
                indeterminate
                size="16"
                width="2"
                color="#047857"
                class="mr-1"
              />
              확정 해제
            </button>
          </template>
          <template v-else>
            <button class="btn-outline confirm-emphasis" @click="handleConfirm" :disabled="!hasUploaded || isConfirming">
              <v-progress-circular
                v-if="isConfirming"
                indeterminate
                size="16"
                width="2"
                color="#047857"
                class="mr-1"
              />
              데이터 확정
            </button>
          </template>
        </div>
      </div>

      <button v-else class="btn-primary-large" @click="openInitialUpload">
        <v-icon class="btn-icon">mdi-cloud-upload</v-icon>
        <span>파일 업로드하기</span>
      </button>
    </div>

    <!-- 재업로드 확인 다이얼로그 -->
    <v-dialog v-model="showReuploadConfirm" max-width="440px">
      <v-card class="confirm-dialog">
        <div class="confirm-dialog-header">
          <v-icon size="48" color="#f59e0b">mdi-alert-circle</v-icon>
          <h3>파일 재업로드</h3>
        </div>
        <div class="confirm-dialog-body">
          <p>기존에 업로드된 데이터는 삭제됩니다.</p>
          <p>정말 재업로드하시겠습니까?</p>
        </div>
        <div class="confirm-dialog-footer">
          <button class="btn-cancel" @click="showReuploadConfirm = false">취소</button>
          <button class="btn-confirm" @click="proceedReupload">재업로드</button>
        </div>
      </v-card>
    </v-dialog>

    <!-- 확정 확인 다이얼로그 -->
    <v-dialog v-model="showConfirmDialog" max-width="440px">
      <v-card class="confirm-dialog">
        <div class="confirm-dialog-header">
          <v-icon size="48" color="#10b981">mdi-lock-check</v-icon>
          <h3>데이터 확정</h3>
        </div>
        <div class="confirm-dialog-body">
          <p>데이터를 확정하시겠습니까?</p>
          <p>확정 후에는 데이터를 수정하거나 재업로드할 수 없습니다.</p>
        </div>
        <div class="confirm-dialog-footer">
          <button class="btn-cancel" @click="showConfirmDialog = false">취소</button>
          <button class="btn-confirm" @click="proceedConfirm">확정</button>
        </div>
      </v-card>
    </v-dialog>

    <!-- 확정 해제 다이얼로그 -->
    <v-dialog v-model="showReleaseDialog" max-width="440px">
      <v-card class="confirm-dialog">
        <div class="confirm-dialog-header warning">
          <v-icon size="48" color="#f59e0b">mdi-alert-circle</v-icon>
          <h3>확정 해제</h3>
        </div>
        <div class="confirm-dialog-body">
          <p>데이터 확정을 해제하시겠습니까?</p>
          <p>해제 후에는 데이터를 다시 수정하거나 재업로드할 수 있습니다.</p>
        </div>
        <div class="confirm-dialog-footer">
          <button class="btn-cancel" @click="showReleaseDialog = false">취소</button>
          <button class="btn-confirm warning" @click="proceedRelease">해제</button>
        </div>
      </v-card>
    </v-dialog>

    <!-- 일괄 업로드 다이얼로그 -->
    <v-dialog v-model="showBulkUploadDialog" max-width="560px" :persistent="isBulkUploading">
      <v-card class="upload-modal">
        <div class="upload-modal-header">
          <h2>파일 업로드</h2>
          <button class="close-btn" @click="closeBulkUpload" :disabled="isBulkUploading">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>
        
        <div class="upload-modal-body">
          <div class="upload-notice">
            <v-icon size="18" color="#1e40af">mdi-information</v-icon>
            <span v-if="isReuploadMode">수정이 필요한 파일만 업로드하세요</span>
            <span v-else><strong>청구내역</strong>, <strong>청구내역상세</strong> 파일 2개를 모두 업로드하세요</span>
          </div>

          <div 
            class="dropzone-area"
            :class="{ 'is-active': isDragging, 'is-disabled': isBulkUploading }"
            @drop.prevent="handleDrop"
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            @click="handleDropzoneClick"
          >
            <div class="dropzone-icon-wrapper">
              <v-icon size="56" :color="isDragging ? '#1e3a8a' : '#94a3b8'">mdi-cloud-upload</v-icon>
            </div>
            <h3 class="dropzone-text">파일을 드래그하거나 클릭하세요</h3>
            <p class="dropzone-hint">엑셀 파일 (xls, xlsx)</p>
          </div>
          
          <input
            ref="bulkFileInput"
            type="file"
            multiple
            accept=".xls,.xlsx"
            style="display: none"
            @change="handleBulkFileSelect"
          />
          
          <transition name="fade">
            <div v-if="bulkFiles.length > 0" class="files-section">
              <div class="files-section-header">
                <span class="files-count">선택된 파일 {{ bulkFiles.length }}개</span>
                <button class="clear-btn" @click="clearAllFiles" :disabled="isBulkUploading">
                  <v-icon size="16">mdi-delete-outline</v-icon>
                  전체 삭제
                </button>
              </div>
              <div class="files-list">
                <transition-group name="list">
                  <div v-for="(file, index) in bulkFiles" :key="file.name + index" class="file-card">
                    <div class="file-card-icon">
                      <v-icon color="#10b981" size="28">mdi-file-excel</v-icon>
                    </div>
                    <div class="file-card-info">
                      <span class="file-card-name">{{ file.name }}</span>
                      <span class="file-card-size">{{ formatFileSize(file.size) }}</span>
                    </div>
                    <button class="file-card-delete" @click="removeFile(index)" :disabled="isBulkUploading">
                      <v-icon size="18">mdi-close</v-icon>
                    </button>
                  </div>
                </transition-group>
              </div>
            </div>
          </transition>

          <transition name="fade">
            <div v-if="!isReuploadMode && bulkFiles.length > 0 && bulkFiles.length < 2" class="upload-warning">
              <v-icon size="18" color="#f59e0b">mdi-alert</v-icon>
              <span>청구내역, 청구내역상세 파일 2개를 모두 선택해주세요</span>
            </div>
          </transition>
        </div>
        
        <div class="upload-modal-footer">
          <button 
            class="btn-cancel" 
            @click="closeBulkUpload"
            :disabled="isBulkUploading"
          >
            취소
          </button>
          <button
            class="btn-upload"
            @click="executeBulkUpload"
            :disabled="(isReuploadMode ? bulkFiles.length === 0 : bulkFiles.length < 2) || isBulkUploading"
          >
            <v-progress-circular
              v-if="isBulkUploading"
              indeterminate
              size="18"
              width="2"
              color="white"
              class="mr-2"
            ></v-progress-circular>
            <span>{{ isBulkUploading ? '업로드 중...' : `${bulkFiles.length}개 파일 업로드` }}</span>
          </button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';

interface BillingRow {
  [key: string]: any;
}

const props = defineProps<{
  uploadedFilesCount: number;
  billingData: BillingRow[];
  billingDetailData: BillingRow[];
  isLoading: boolean;
  activeYear: number;
  activeMonth: number;
  isConfirmed?: boolean;
  isConfirming?: boolean;
}>();

const emit = defineEmits<{
  (e: 'uploaded', count: number): void;
  (e: 'confirm'): void;
  (e: 'release'): void;
}>();

const store = useStore();

const uploadedFilesCount = computed(() => props.uploadedFilesCount || 0);
const hasUploaded = computed(() => uploadedFilesCount.value >= 2);
const billingData = computed(() => props.billingData || []);
const billingDetailData = computed(() => props.billingDetailData || []);
const isLoading = computed(() => !!props.isLoading);
const isConfirmed = computed(() => !!props.isConfirmed);
const isConfirming = computed(() => !!props.isConfirming);
const hasBillingData = computed(() => billingData.value.length > 0);
const hasBillingDetailData = computed(() => billingDetailData.value.length > 0);
const hasAnyData = computed(() => hasBillingData.value || hasBillingDetailData.value);
const totalDataCount = computed(() => billingData.value.length + billingDetailData.value.length);
const periodLabel = computed(() => {
  const year = props.activeYear || new Date().getFullYear();
  const month = String(props.activeMonth || new Date().getMonth() + 1).padStart(2, '0');
  return `${year}.${month}`;
});

// 요약 데이터 계산
const summaryData = computed(() => {
  const uniquePatients = new Set<string>();
  const uniqueCaregivers = new Set<string>();
  let totalSugaMoney = 0;

  // 청구내역상세에서 수급자와 수가 합계 계산
  billingDetailData.value.forEach(row => {
    if (row.lebd_patient_code) {
      uniquePatients.add(row.lebd_patient_code);
    }
    const sugaMoney = Number(row.lebd_suga_money);
    if (!Number.isNaN(sugaMoney)) {
      totalSugaMoney += sugaMoney;
    }
  });

  // 청구내역에서 요양보호사 수 계산
  billingData.value.forEach(row => {
    if (row.leb_caregiver_name) {
      uniqueCaregivers.add(row.leb_caregiver_name);
    }
  });

  return {
    totalPatients: uniquePatients.size,
    totalCaregivers: uniqueCaregivers.size,
    totalSugaMoney
  };
});

const activeTab = ref<'summary' | 'billing' | 'detail'>('summary');
const showReuploadConfirm = ref(false);
const showConfirmDialog = ref(false);
const showReleaseDialog = ref(false);
const showBulkUploadDialog = ref(false);
const bulkFiles = ref<File[]>([]);
const bulkFileInput = ref<HTMLInputElement | null>(null);
const isBulkUploading = ref(false);
const isDragging = ref(false);
const isReuploadMode = ref(false);

watch(
  () => [billingData.value.length, billingDetailData.value.length],
  () => {
    if (hasAnyData.value) {
      activeTab.value = 'summary';
    }
  },
  { immediate: true }
);

const confirmReupload = () => {
  if (isConfirmed.value) {
    store.commit('showSnackbar', {
      text: '확정 상태에서는 재업로드할 수 없어요. 해제 후 다시 시도해주세요.',
      color: 'warning'
    });
    return;
  }
  showReuploadConfirm.value = true;
};

const proceedReupload = () => {
  showReuploadConfirm.value = false;
  openBulkUpload('reupload');
};

const handleConfirm = () => {
  showConfirmDialog.value = true;
};

const proceedConfirm = () => {
  showConfirmDialog.value = false;
  emit('confirm');
};

const handleRelease = () => {
  showReleaseDialog.value = true;
};

const proceedRelease = () => {
  showReleaseDialog.value = false;
  emit('release');
};

const openInitialUpload = () => {
  openBulkUpload('initial');
};

const openBulkUpload = (mode: 'initial' | 'reupload' = 'initial') => {
  isReuploadMode.value = mode === 'reupload';
  showBulkUploadDialog.value = true;
};

const closeBulkUpload = () => {
  if (isBulkUploading.value) return;
  showBulkUploadDialog.value = false;
  bulkFiles.value = [];
  isDragging.value = false;
  isReuploadMode.value = false;
};

const triggerFileInput = () => {
  bulkFileInput.value?.click();
};

const handleDropzoneClick = () => {
  if (isBulkUploading.value) return;
  triggerFileInput();
};

const handleDragOver = () => {
  if (isBulkUploading.value) return;
  isDragging.value = true;
};

const handleDragLeave = () => {
  if (isBulkUploading.value) return;
  isDragging.value = false;
};

const handleDrop = (event: DragEvent) => {
  if (isBulkUploading.value) return;
  isDragging.value = false;
  const files = event.dataTransfer?.files;
  if (files) {
    addFiles(Array.from(files));
  }
};

const handleBulkFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const files = target.files;
  if (files) {
    addFiles(Array.from(files));
  }
  target.value = '';
};

const addFiles = (newFiles: File[]) => {
  const excelFiles = newFiles.filter(file => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    return ext === 'xls' || ext === 'xlsx';
  });
  
  if (excelFiles.length < newFiles.length) {
    store.commit('showSnackbar', {
      text: '엑셀 파일만 업로드 가능해요',
      color: 'warning'
    });
  }
  
  excelFiles.forEach(file => {
    const isDuplicate = bulkFiles.value.some(f => f.name === file.name && f.size === file.size);
    if (!isDuplicate) {
      bulkFiles.value.push(file);
    }
  });
};

const removeFile = (index: number) => {
  bulkFiles.value.splice(index, 1);
};

const clearAllFiles = () => {
  bulkFiles.value = [];
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

const executeBulkUpload = async () => {
  if (bulkFiles.value.length === 0) return;
  
  // 연월 검증을 위한 변수
  const expectedYear = props.activeYear;
  const expectedMonth = props.activeMonth;
  const expectedYM = `${expectedYear}${String(expectedMonth).padStart(2, '0')}`;
  
  isBulkUploading.value = true;
  const user = store.state.user;
  
  let successCount = 0;
  let failCount = 0;
  const errorMessages: string[] = [];
  
  for (const file of bulkFiles.value) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('year', expectedYear.toString());
      formData.append('institutionCode', user.m_sisul_code);
      
      const response = await axios.post('/api/files/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true
      });
      
      if (response.data.success) {
        const fileYM = response.data.fileYearMonth;
        
        // 파일 내 연월과 선택된 연월이 다른 경우
        if (fileYM && fileYM !== expectedYM) {
          const fileYear = fileYM.substring(0, 4);
          const fileMonth = fileYM.substring(4, 6);
          errorMessages.push(
            `${file.name}: 파일 내 연월(${fileYear}년 ${parseInt(fileMonth)}월)이 선택된 연월(${expectedYear}년 ${expectedMonth}월)과 다릅니다.`
          );
          failCount++;
        } else {
          successCount++;
        }
      } else {
        failCount++;
        errorMessages.push(`${file.name}: ${response.data.message || '업로드 실패'}`);
      }
    } catch (error: any) {
      failCount++;
      const errorMsg = error.response?.data?.message || error.message || '알 수 없는 오류';
      errorMessages.push(`${file.name}: ${errorMsg}`);
      console.error('업로드 오류:', error);
    }
  }
  
  isBulkUploading.value = false;
  
  // 재업로드 모드일 경우
  if (isReuploadMode.value) {
    if (successCount > 0) {
      store.commit('showSnackbar', {
        text: `✅ ${successCount}개 파일이 재업로드되었어요!`,
        color: 'success'
      });
      emit('uploaded', successCount);
      closeBulkUpload();
    } else {
      // 에러 메시지 표시
      const errorText = errorMessages.length > 0 
        ? errorMessages.join('\n') 
        : '파일 업로드에 실패했어요';
      store.commit('showSnackbar', {
        text: errorText,
        color: 'error'
      });
    }
  } else {
    // 최초 업로드 모드일 경우
    if (successCount >= 2) {
      store.commit('showSnackbar', {
        text: `✅ ${successCount}개 파일이 업로드되었어요!`,
        color: 'success'
      });
      emit('uploaded', successCount);
      closeBulkUpload();
    } else if (successCount > 0) {
      store.commit('showSnackbar', {
        text: `${successCount}개 파일이 업로드되었지만, 청구내역, 청구내역상세 파일 2개가 모두 필요해요`,
        color: 'warning'
      });
    } else {
      // 에러 메시지 표시
      const errorText = errorMessages.length > 0 
        ? errorMessages.join('\n') 
        : '파일 업로드에 실패했어요';
      store.commit('showSnackbar', {
        text: errorText,
        color: 'error'
      });
    }
  }
};

const formatDate = (value?: string | number | null) => {
  if (!value) return '-';
  const str = value.toString();
  if (str.includes('-')) return str;
  if (str.length === 8) {
    return `${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`;
  }
  return str;
};

const formatTime = (value?: string | number | null) => {
  if (!value) return '-';
  const str = value.toString().replace(/[^0-9]/g, '');
  if (str.length === 4) {
    return `${str.slice(0, 2)}:${str.slice(2, 4)}`;
  }
  return value.toString();
};

const formatYesNo = (value?: string | null) => {
  if (!value) return '-';
  return value === 'Y' ? '예' : value === 'N' ? '아니오' : value;
};

const formatCurrency = (value?: string | number | null) => {
  if (value === undefined || value === null || value === '') return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return value.toString();
  return `${num.toLocaleString()}원`;
};


</script>

<style scoped lang="scss">
@import '@/assets/styles/wageProcess.scss';
@import '@/assets/styles/StepTwo.scss';
</style>